import { Injectable } from '@angular/core';
import { GetClientSetting } from '@app/api-module/control-centre-api-client';
import { Tab } from '@app/models/tab';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { Org } from 'src/app/models';
import { ClientSite } from 'src/app/models/clientsite';

@Injectable({
    providedIn: 'root'
})
export class OrgSiteSelectionService {
    private readonly selectedOrgCode$ = new BehaviorSubject<string>(null);
    private readonly selectedOrgSettings$ = new BehaviorSubject<GetClientSetting[]>([]);
    private readonly selectedTab$ = new BehaviorSubject<Tab | null>(null);

    private readonly _allowedOrgsSource = new BehaviorSubject<Org[]>([]);
    readonly allowedOrgs$ = this._allowedOrgsSource.asObservable().pipe(
        filter(orgs => orgs != undefined && orgs != null && orgs.length > 0),
        shareReplay()
    );

    private readonly _orgSelected$ = combineLatest([this._allowedOrgsSource, this.selectedOrgCode$]).pipe(
        map(([allowedOrgs, selectedOrgCode]) => this.getOrgByOrgCode(allowedOrgs, selectedOrgCode))
    );
    private getOrgByOrgCode(orgs: Org[], orgCode: string): Org {
        return orgs.find(org => org.orgCode === orgCode);
    }
    getOrgSelected$ = () =>
        this._orgSelected$.pipe(
            filter(org => org != undefined && org != null),
            shareReplay()
        );
    getOrgSelectedSettings$ = () => this.selectedOrgSettings$.pipe(shareReplay());

    private readonly _allSitesSource = new BehaviorSubject<ClientSite[]>([]);
    getAllSites = () =>
        this._allSitesSource.asObservable().pipe(
            filter(sites => sites.length > 0),
            shareReplay()
        );

    private readonly _allowedSitesSource = new BehaviorSubject<[ClientSite[], number]>([[], -1]);
    getAllowedSites = () =>
        this._allowedSitesSource.asObservable().pipe(
            filter(([sites, defaultSiteId]) => sites.length > 0 && defaultSiteId != -1),
            shareReplay()
        );

    private readonly _sitesSelectedSource = new BehaviorSubject<number[]>([]);
    getSitesSelected = () =>
        this._sitesSelectedSource.asObservable().pipe(
            filter(siteId => siteId.length > 0),
            shareReplay()
        );

    private readonly _hasOnlyOneSiteSource = new BehaviorSubject<boolean>(false);
    readonly hasOnlyOneSite = () => this._hasOnlyOneSiteSource.asObservable().pipe(shareReplay());

    getHasOnlyOneSiteFlag() {
        return this._hasOnlyOneSiteSource.value;
    }

    getOrgs() {
        return this._allowedOrgsSource.value;
    }

    updateOrgs(orgs: Org[]) {
        this._allowedOrgsSource.next(orgs);
    }

    selectOrg(org: Org, forceSelect: boolean) {
        if (org.orgCode != this.selectedOrgCode$.value || forceSelect) {
            this.forceSelectOrg(org);
        }
    }

    forceSelectOrg(org: Org) {
        this.selectedOrgCode$.next(org.orgCode);
        this.selectedOrgSettings$.next(org.settings);
        if (org.defaultSiteId) {
            this.selectSites([org.defaultSiteId]);
        } else {
            const { allowedSites } = org.getAllowedSites();
            // 'null' is a standin for 'all available sites'
            this.selectSites(allowedSites.length > 0 ? [...allowedSites] : [null]);
        }
    }

    getOrgSelectedValue() {
        return this.getOrgByOrgCode(this._allowedOrgsSource.value, this.selectedOrgCode$.value);
    }

    updateAllSites(sites: ClientSite[]) {
        this._allSitesSource.next(sites);
    }

    updateAllowedSites(sites: ClientSite[], defaultSiteId: number) {
        this._allowedSitesSource.next([sites, defaultSiteId]);
    }

    selectSites(siteIds: number[]) {
        if (siteIds.length > 0 && this.sitesAreDifferentToCurrent(siteIds)) {
            this._sitesSelectedSource.next(siteIds);
        }
    }

    getSelectedSitesValue() {
        return this._sitesSelectedSource.value;
    }

    setHasOnlyOneSite(hasOnlyOneSite: boolean) {
        this._hasOnlyOneSiteSource.next(hasOnlyOneSite);
    }

    getTabSync(): Tab {
        return this.selectedTab$.value;
    }

    getTab(): Observable<Tab> {
        return this.selectedTab$.pipe(distinctUntilChanged());
    }

    setTab(tab: Tab): void {
        if (tab !== Tab.Unknown) {
            this.selectedTab$.next(tab);
        }
    }

    private sitesAreDifferentToCurrent(sites: number[]): boolean {
        const currentSites = new Set(this._sitesSelectedSource.value);
        return !(sites.length === currentSites.size && sites.every(siteId => currentSites.has(siteId)));
    }
}
